import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import logger from '../utils/logger';

const NotificationContext = createContext(null);

// Singleton ref for use outside React components (e.g. apiErrorHandler)
let globalNotify = null;

export const getNotify = () => globalNotify;

const ICONS = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  warn: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const STYLES = {
  success: {
    bg: 'bg-green-50 dark:bg-green-950/50',
    border: 'border-green-200 dark:border-green-800/60',
    text: 'text-green-800 dark:text-green-200',
    icon: 'text-green-500 dark:text-green-400',
    close: 'text-green-400 hover:text-green-600 dark:text-green-500 dark:hover:text-green-300',
    progress: 'bg-green-500',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950/50',
    border: 'border-red-200 dark:border-red-800/60',
    text: 'text-red-800 dark:text-red-200',
    icon: 'text-red-500 dark:text-red-400',
    close: 'text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300',
    progress: 'bg-red-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/50',
    border: 'border-amber-200 dark:border-amber-800/60',
    text: 'text-amber-800 dark:text-amber-200',
    icon: 'text-amber-500 dark:text-amber-400',
    close: 'text-amber-400 hover:text-amber-600 dark:text-amber-500 dark:hover:text-amber-300',
    progress: 'bg-amber-500',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/50',
    border: 'border-blue-200 dark:border-blue-800/60',
    text: 'text-blue-800 dark:text-blue-200',
    icon: 'text-blue-500 dark:text-blue-400',
    close: 'text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300',
    progress: 'bg-blue-500',
  },
};

const Notification = ({ id, message, type, onClose, duration }) => {
  const normalizedType = type === 'warn' ? 'warning' : type;
  const s = STYLES[normalizedType] || STYLES.info;
  const Icon = ICONS[type] || ICONS.info;

  return (
    <div
      className={`w-full max-w-sm ${s.bg} ${s.border} border rounded-xl p-3.5 shadow-lg backdrop-blur-sm animate-slide-in-right overflow-hidden`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.icon}`} />
        <p className={`text-sm font-medium flex-1 leading-snug ${s.text}`}>{message}</p>
        <button
          onClick={() => onClose(id)}
          className={`${s.close} transition-colors p-0.5 rounded-md hover:bg-black/5 dark:hover:bg-white/5`}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      {/* Progress bar */}
      <div className="mt-2.5 h-0.5 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full ${s.progress} rounded-full`}
          style={{
            animation: `shrink ${duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef({});
  const idRef = useRef(0);

  const removeNotification = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++idRef.current;

    setNotifications(prev => {
      // Max 5 notifications at once
      const updated = prev.length >= 5 ? prev.slice(1) : prev;
      return [...updated, { id, message, type, duration }];
    });

    timersRef.current[id] = setTimeout(() => {
      removeNotification(id);
    }, duration);

    return id;
  }, [removeNotification]);

  // Expose globally for non-React code
  globalNotify = notify;

  return (
    <NotificationContext.Provider value={notify}>
      {children}

      {/* Notification container */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2.5 pointer-events-auto">
          {notifications.map(n => (
            <Notification
              key={n.id}
              id={n.id}
              message={n.message}
              type={n.type}
              duration={n.duration}
              onClose={removeNotification}
            />
          ))}
        </div>
      )}

      {/* Keyframe animation */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.25s ease-out;
        }
      `}</style>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Fallback for components outside provider — use global ref
    return globalNotify || ((msg) => logger.warn('[Notification] No provider:', msg));
  }
  return ctx;
};

export default NotificationContext;
